import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { apiGet, fmtDate } from '../api';
import { theme, card, btnSecondary } from '../theme';
import PageHeading from '../components/PageHeading';
import { FileText, ChevronLeft, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';

// v0.330.0 — a builder's report reaches a client's home only through the
// client's OWN share link (/r/<token>). Anything else the server might send is
// not drawn as a link: a path we did not make is never followed.
export function safeOpenUrl(u) {
  return typeof u === 'string' && /^\/r\/[A-Za-z0-9_-]+$/.test(u) ? u : null;
}

// ---------------------------------------------------------------------------
// ClientLocationReportsScreen — read-only reports for ONE Location, shown to a
// CLIENT hand-off session (the signed droppin_contact cookie).
//
// Route: /projects/:projectId/site-reports
//
// WHY A DEDICATED CLIENT SCREEN
//   A client arriving via the InSpec portal hand-off has NO insite_sid, so the
//   staff report screens (/projects/:id, /projects/:id/reports, ReportScreen)
//   all 401 — every one resolves its tenant from the staff cookie and calls the
//   admin /api/projects + /api/defects + /api/reports endpoints. Clicking a
//   Location on the client Locations screen therefore did nothing.
//
//   This screen is the client-safe destination for that click. It reads ONE
//   round-trip from /api/contact-reports?projectId=N (contact-cookie auth,
//   strict tenant scoping) and lists the site's saved reports read-only, with
//   the server-computed defect summary per report. No create / edit / delete —
//   clients view, they don't edit. AppShell keeps a client on any /projects/*
//   path, so this route is reachable without tripping the client redirect.
// ---------------------------------------------------------------------------
export default function ClientLocationReportsScreen() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [reports, setReports] = useState([]);
  // v0.178.0 — how many reports exist at this site that are NOT shared with
  // this reader. A COUNT only: no names, no dates, no item totals. A site that
  // quietly looks emptier than it is invites a confused phone call; a number
  // invites a request, which is the conversation we want.
  // v0.330.0 — null for a builder's site (h…): the client is never told what
  // else a builder holds there, so the footnote is not drawn.
  const [hiddenCount, setHiddenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError('');
    try {
      const data = await apiGet(`/api/contact-reports?projectId=${encodeURIComponent(projectId)}`);
      setProject(data?.project || null);
      setReports(Array.isArray(data?.reports) ? data.reports : []);
      setHiddenCount(typeof data?.hiddenCount === 'number' ? data.hiddenCount : 0);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const backToLocations = () => navigate('/projects');

  return (
    <div>
      <button
        onClick={backToLocations}
        style={{ ...btnSecondary, marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <ChevronLeft size={15} /> Locations
      </button>

      {/* Location header — name, status, address + the site-wide defect
          progress, so the page stands on its own without the card view. */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <PageHeading style={{ margin: 0 }}>{project?.name || (loading ? 'Loading…' : 'Location')}</PageHeading>
              {project?.status && String(project.status).toLowerCase() !== 'active' && <StatusPill status={project.status} />}
            </div>
            {project?.address && (
              <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>{project.address}</div>
            )}
            {project?.sharedBy && (
              <div data-shared-by-site style={{ fontSize: 13, fontWeight: 700, color: theme.text, marginTop: 4 }}>
                Shared with you by {project.sharedBy}
              </div>
            )}
            <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>
              Reports for this site. Tap-free, read-only — this is what your builder has prepared.
            </div>
          </div>
          {project && <SiteProgress p={project} />}
        </div>
      </div>

      {error && (
        <SyncStateCard
          icon={<AlertTriangle size={44} />}
          title="Couldn't load this site's reports"
          body={error}
          actionLabel="Try again"
          onAction={load}
        />
      )}

      {!error && loading ? (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>Loading reports…</div>
      ) : !error && reports.length === 0 ? (
        <EmptyState />
      ) : !error ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {reports.map(r => (
            <ReportRow key={r.id} r={r} />
          ))}
        </div>
      ) : null}

      {/* v0.178.0 — the honest footnote. Worded as "not shared with you yet",
          not "hidden": nothing has been taken away from this reader, and the
          builder may simply not have finished. Says who to ask, because the
          only useful next step is asking them. */}
      {!error && !loading && hiddenCount > 0 && (
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 8,
          border: `1px solid ${theme.border}`, background: theme.surface,
          fontSize: 12, color: theme.textLight, lineHeight: 1.55,
        }}>
          {hiddenCount} other report{hiddenCount === 1 ? '' : 's'} at this site {hiddenCount === 1 ? 'has' : 'have'} not been shared with you yet.
          {' '}Ask your builder if you need {hiddenCount === 1 ? 'it' : 'them'}.
        </div>
      )}
    </div>
  );
}

// Read-only report row. Mirrors the admin ReportsScreen row visual language
// (name + description + meta on the left, progress on the right) but carries
// no actions — a client views, never edits.
function ReportRow({ r }) {
  // v0.330.0 — a report a builder shared with this person by name: who shared
  // it, and Open through their own link (the report page, its chat and PDF).
  const openUrl = r.sharedBy ? safeOpenUrl(r.openUrl) : null;
  const total = Number(r.defectsTotal || 0);
  const done = Number(r.defectsDone || 0);
  const open = Number(r.defectsOpen ?? (total - done));
  return (
    <div style={{ ...card, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <FileText size={16} color={theme.brand} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 15, fontWeight: 800, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {r.name || 'Untitled report'}
          </span>
        </div>
        {r.description && (
          <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>{r.description}</div>
        )}
        {r.sharedBy && (
          <div data-shared-by={r.id} style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginTop: 4 }}>
            Shared with you by {r.sharedBy}
          </div>
        )}
        <div style={{ fontSize: 11, color: theme.textLight, marginTop: 8 }}>
          {r.preparedBy ? <>Prepared by {r.preparedBy} · </> : null}
          Created {fmtDate(r.createdAt)}
          {r.updatedAt && r.updatedAt !== r.createdAt ? ` · updated ${fmtDate(r.updatedAt)}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {total > 0 ? (
          <>
            <Pill fg={theme.warn}>{open} open</Pill>
            <Pill fg={theme.textLight}>{total} total</Pill>
            {done === total && <Pill fg={theme.success}>complete</Pill>}
          </>
        ) : (
          <Pill fg={theme.textLight}>no defects</Pill>
        )}
        {openUrl && (
          <a
            href={openUrl}
            data-open-report={r.id}
            style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px', fontSize: 12, textDecoration: 'none', marginLeft: 4 }}
          >
            Open <ExternalLink size={12} aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}

// Site-wide defect progress for the header. Same 3-state model + progress bar
// as the admin ProjectsScreen DefectStat, using theme tokens only.
function SiteProgress({ p }) {
  const total = Number(p.defectsTotal || 0);
  if (total === 0) {
    return <span style={{ color: theme.textLight, fontSize: 12 }}>No defects logged yet</span>;
  }
  const done = Number(p.defectsDone || 0);
  const open = Number(p.defectsOpen ?? (total - done));
  const pct = Math.round((done / total) * 100);
  return (
    <div style={{ minWidth: 160, textAlign: 'right' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 6 }}>
        <span style={{ fontWeight: 700, fontFamily: 'monospace', color: theme.text, fontSize: 14 }}>{open}</span>
        <span style={{ fontSize: 11, color: theme.textLight }}>open</span>
        <span style={{ fontSize: 11, color: theme.textLight }}>/</span>
        <span style={{ fontWeight: 600, fontFamily: 'monospace', color: theme.textLight, fontSize: 13 }}>{total}</span>
        <span style={{ fontSize: 11, color: theme.textLight }}>total</span>
      </div>
      <div style={{ marginTop: 4, height: 5, background: theme.border, borderRadius: 3, overflow: 'hidden', width: '100%', maxWidth: 170, marginLeft: 'auto' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? theme.success : theme.brand }} />
      </div>
      <div style={{ fontSize: 10, color: theme.textLight, marginTop: 2 }}>{pct}% done</div>
    </div>
  );
}

function Pill({ fg, children }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
      background: theme.bg, color: fg, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function StatusPill({ status }) {
  const map = {
    active:   { label: 'Active',   fg: theme.success },
    archived: { label: 'Archived', fg: theme.textLight },
    complete: { label: 'Complete', fg: theme.brand },
  };
  const m = map[status] || { label: status, fg: theme.textLight };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: theme.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div style={{ ...card, padding: 48, textAlign: 'center' }}>
      <FileText size={44} color={theme.textLight} strokeWidth={1.5} />
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}>No reports for this site yet</div>
      <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        When your builder prepares a report for this location it will appear here.
      </div>
    </div>
  );
}

// Honest error card — mirrors the ProjectsScreen SyncStateCard so a failed
// load reads as a clear, recoverable state, not a dead end.
function SyncStateCard({ icon, title, body, actionLabel, onAction }) {
  return (
    <div style={{ ...card, textAlign: 'center', padding: 48 }}>
      <div style={{ display: 'flex', justifyContent: 'center', color: theme.danger, opacity: 0.9, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
      {body && (
        <div style={{ fontSize: 13, color: theme.textLight, marginTop: 6, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', wordBreak: 'break-word' }}>{body}</div>
      )}
      {onAction && (
        <button onClick={onAction} style={{ ...btnSecondary, marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> {actionLabel || 'Try again'}
        </button>
      )}
    </div>
  );
}
